import { useMutation } from '@tanstack/react-query'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { useCallback, useEffect } from 'react'
import BackNavLink from 'core/components/navigation/BackNavLink'
import APIService from 'api/APIService'
import { AuthFormValue } from 'pages/auth/type'
import Button from 'core/components/buttons/Button'
import SmartField from 'core/components/form/SmartField'
import Input from 'core/components/inputs/Input'
import { SendPasswordResetUrl, StartUrl } from 'constants/RouteUrls'
import useBlueBackground from 'pages/sign-up/useBlueBackground'
import { Link } from 'react-router'
import useNotifications from 'core/hooks/ui/useNotifications'
import useNavigation from 'core/hooks/navigation/useNavigation'
import { stashPendingRedirect } from 'core/hooks/auth/pendingRedirect'

const PurpleSSOPage = () => {
  useBlueBackground()
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors }
  } = useFormContext<AuthFormValue>()
  const notifications = useNotifications()
  const { search } = useNavigation()

  useEffect(() => {
    stashPendingRedirect(search)
  }, [search])

  const { isPending: isLoadingMotusSSO, mutate: mutateMotusSSO } = useMutation({
    mutationKey: ['motus', 'sso', 'url'],
    mutationFn: APIService.motus.getSsoRedirectUrl,
    onSuccess: (data) => {
      window.location.assign(data.ssoWebUrl)
    },
    onError: () => {
      setError('root.error', {
        type: '',
        message: 'Could not login. Please try again'
      })
      notifications.error({
        title: 'Login error',
        subtitle: 'Please try again later'
      })
    }
  })

  const onSubmit: SubmitHandler<AuthFormValue> = useCallback(
    ({ email }) => {
      notifications.dismissAll()
      clearErrors()
      mutateMotusSSO({
        email
      })
    },
    [mutateMotusSSO, notifications, clearErrors]
  )

  return (
    <div
      data-title="login-screen"
      className="h-[88vh] flex justify-around items-center"
    >
      <div className="card bg-white mb-5 w-[500px]">
        <BackNavLink to={StartUrl} className="text-primary" />
        <div className="my-20 px-14">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-10 mt-6">
              <h3 className="text-4xl mb-4 text-primary font-bold">
                Sign In with SSO
              </h3>
              <p className="text-lg">
                Complete the text field with the credentials from your invite.
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <SmartField
                controllerProps={{
                  control,
                  name: 'email',
                  rules: {
                    required: 'Email is required',
                    validate: {
                      noBlank: (value) =>
                        value.trim().length > 0 ||
                        'Email cannot be just whitespace'
                    }
                  }
                }}
                label="Email"
                orientation="vertical"
                requirementDecoration={null}
              >
                <Input
                  placeholder="email@email.com"
                  className="text-lg mb-2 w-full py-3.5"
                />
              </SmartField>
              <Link
                to={SendPasswordResetUrl}
                className="select-none text-primary underline"
              >
                Forgot Password?
              </Link>

              <Button
                type="submit"
                variant="primary"
                status={isLoadingMotusSSO ? 'loading' : 'idle'}
                disabled={isLoadingMotusSSO}
                title="submit-button"
                className="w-full mt-2"
              >
                Sign In
              </Button>
            </div>

            {!!errors.root?.message && (
              <div>
                <p className="pt-2 text-danger">{errors.root?.message}</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}

export default PurpleSSOPage
